import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  FaSchool, 
  FaChalkboardTeacher, 
  FaUserGraduate, 
  FaCalendarAlt, 
  FaChartBar, 
  FaCog, 
  FaBell, 
  FaSignOutAlt, 
  FaUserCircle,
  FaInbox,
  FaClipboardList,
  FaMoneyBillWave,
  FaCrown,
  FaShoppingCart,
  FaWhatsapp,
  FaGlobe,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import SchoolRoutes from '../../routes/schoolRoutes';

/**
 * SchoolPanel component - Layout wrapper for all school administration pages
 */
const SchoolPanel = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);

  // Check authentication on component mount
  useEffect(() => {
    if (!isAuthenticated || (user && !['system_admin', 'school_admin'].includes(user.role))) {
      navigate('/login', { state: { message: 'Please log in as a school administrator to access this page' } });
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!isAuthenticated || !user) return;
      try {
        // Replace with actual endpoint when available
        // const res = await api.get('/school/notifications');
        // setNotifications(res?.data?.data || []);
        setNotifications([]);
      } catch (err) {
        setNotifications([]);
      }
    };
    fetchNotifications();
  }, [isAuthenticated, user]);

  // Handle notification click
  const handleNotificationClick = (notificationId) => {
    // Mark notification as read
    setNotifications(prevNotifications => 
      prevNotifications.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true } 
          : notification
      )
    );
    // Close notification dropdown
    setIsNotificationOpen(false);
  };

  // Format date for display
  const formatDate = (date) => {
    const now = new Date();
    const diff = now - date;
    
    // Less than a day
    if (diff < 86400000) {
      // Less than an hour
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      }
      
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }
    
    // Less than a week
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
    
    return date.toLocaleDateString();
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
    showToast('Logged out successfully', 'success');
  };

  const toggleSidebarSize = () => {
    setIsSidebarMinimized(!isSidebarMinimized);
  };

  // Navigation links for the sidebar
  const navigationLinks = [
    { name: 'Dashboard', to: '/school', icon: <FaSchool className="mr-3 h-5 w-5" /> },
    { name: 'Teachers', to: '/school/teachers', icon: <FaChalkboardTeacher className="mr-3 h-5 w-5" /> },
    { name: 'Students', to: '/school/students', icon: <FaUserGraduate className="mr-3 h-5 w-5" /> },
    { name: 'Classes', to: '/school/classes', icon: <FaCalendarAlt className="mr-3 h-5 w-5" /> },
    { name: 'Attendance', to: '/school/attendance', icon: <FaClipboardList className="mr-3 h-5 w-5" /> },
    { name: 'Reports', to: '/school/reports', icon: <FaChartBar className="mr-3 h-5 w-5" /> },
    { name: 'Messages', to: '/school/messages', icon: <FaInbox className="mr-3 h-5 w-5" /> },
    { name: 'Shop', to: '/school/ecommerce', icon: <FaShoppingCart className="mr-3 h-5 w-5" /> },
    { name: 'WhatsApp', to: '/school/whatsapp', icon: <FaWhatsapp className="mr-3 h-5 w-5" /> },
    { name: 'Domains', to: '/school/domains', icon: <FaGlobe className="mr-3 h-5 w-5" /> },
    { name: 'Billing', to: '/school/billing', icon: <FaMoneyBillWave className="mr-3 h-5 w-5" /> },
    { name: 'Settings', to: '/school/settings', icon: <FaCog className="mr-3 h-5 w-5" /> },
    { name: 'Subscription', to: '/school/subscription', icon: <FaCrown className="mr-3 h-5 w-5" /> }
  ];

  // If not authenticated yet, show loading
  if (!isAuthenticated) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>School Administration | Meira</title>
        <meta name="description" content="School administration panel for managing teachers, students, classes, and reports." />
      </Helmet>

      <div className="h-screen flex overflow-hidden bg-gray-100">
        {/* Mobile sidebar overlay */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 z-40 bg-gray-600 bg-opacity-75"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}

        {/* Sidebar */}
        <div 
          className={`${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0 fixed inset-y-0 left-0 flex flex-col z-50 ${
            isSidebarMinimized ? 'md:w-16' : 'md:w-64'
          } transition-all duration-300 transform bg-primary text-white md:static md:inset-0 md:translate-x-0`}
        >
          <div className="flex items-center justify-between flex-shrink-0 px-4 py-4 bg-primary-dark">
            
              <img className="h-8 w-auto" src="https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/meira_logo_design_transparent_cj7blv.png" alt="Meira Logo" />
            
            <div className="flex items-center">
              <button
                className="hidden md:block rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white mr-1"
                onClick={toggleSidebarSize}
              >
                {isSidebarMinimized ? <FaChevronRight /> : <FaChevronLeft />}
              </button>
              <button
                className="md:hidden rounded-md text-white focus:outline-none focus:ring-2 focus:ring-white"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="overflow-y-auto flex-grow">
            <nav className={`mt-5 ${isSidebarMinimized ? 'px-1' : 'px-2'} space-y-1`}>
              {navigationLinks.map((link) => {
                const isActive = location.pathname === link.to || 
                  (link.to !== '/school' && location.pathname.startsWith(link.to));
                
                return (
                  <Link
                    key={link.name}
                    to={link.to}
                    className={`${
                      isActive
                        ? 'bg-primary-dark text-white'
                        : 'text-white hover:bg-primary-light'
                    } group flex items-center ${isSidebarMinimized ? 'justify-center px-1' : 'px-2'} py-2 text-base font-medium rounded-md transition-colors`}
                    title={isSidebarMinimized ? link.name : ''}
                  >
                    <div className={isSidebarMinimized ? 'mx-auto' : ''}>
                      {React.cloneElement(link.icon, { className: isSidebarMinimized ? 'h-5 w-5' : link.icon.props.className })}
                    </div>
                    {!isSidebarMinimized && <span>{link.name}</span>}
                  </Link>
                );
              })}
            </nav>
          </div>
          
          <div className="flex-shrink-0 p-4 border-t border-primary-dark">
            <div className={`flex ${isSidebarMinimized ? 'justify-center' : 'items-center'}`}>
              {!isSidebarMinimized && (
                <>
                  <div>
                    <FaUserCircle className="h-9 w-9 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-white">{user?.name || 'School Admin'}</p>
                    <button
                      onClick={handleLogout}
                      className="text-xs text-gray-300 hover:text-white flex items-center"
                    >
                      <FaSignOutAlt className="mr-1 h-3 w-3" />
                      Sign out
                    </button>
                  </div>
                </>
              )}
              {isSidebarMinimized && (
                <button
                  onClick={handleLogout}
                  className="text-white hover:text-gray-300"
                  title="Sign out"
                >
                  <FaSignOutAlt className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-col w-0 flex-1 overflow-hidden">
          <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
            <button
              type="button"
              className="px-4 md:hidden"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <svg className="h-6 w-6 text-gray-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div className="flex-1 px-4 flex justify-end">
              <div className="ml-4 flex items-center md:ml-6">
                {/* Notification bell */}
                <div className="relative ml-3">
                  <button
                    className="relative p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500"
                    onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                  >
                    <FaBell className="h-6 w-6" />
                    {notifications.filter(n => !n.read).length > 0 && (
                      <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white"></span>
                    )}
                  </button>
                  
                  {/* Notification dropdown */}
                  {isNotificationOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <div className="px-4 py-2 border-b border-gray-200">
                          <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
                        </div>
                        
                        {notifications.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            No new notifications
                          </div>
                        ) : (
                          <div className="max-h-60 overflow-y-auto">
                            {notifications.map((notification) => (
                              <button
                                key={notification.id}
                                className={`px-4 py-3 w-full text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                                  notification.read ? 'bg-white' : 'bg-blue-50'
                                }`}
                                onClick={() => handleNotificationClick(notification.id)}
                              >
                                <p className={`text-sm ${notification.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatDate(notification.date)}
                                </p>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        <div className="px-4 py-2 border-t border-gray-200">
                          <Link
                            to="/school/notifications"
                            className="text-xs text-primary font-medium hover:text-primary-dark"
                            onClick={() => setIsNotificationOpen(false)}
                          >
                            View all notifications
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* User menu */}
                <div className="ml-3 relative">
                  <button
                    className="max-w-xs flex items-center text-sm rounded-full text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.name?.charAt(0) || 'A'}
                      </span>
                    </div>
                  </button>
                  
                  {/* User dropdown */}
                  {isUserMenuOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <Link
                          to="/school/profile"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Your Profile
                        </Link>
                        <Link
                          to="/school/settings"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Settings
                        </Link>
                        <button
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={handleLogout}
                        >
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Page content */}
          <main className="flex-1 relative overflow-y-auto focus:outline-none bg-gray-100">
            <div className="py-6">
              <SchoolRoutes />
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default SchoolPanel;
